import { action, computed } from 'mobx';
import { observer } from 'mobx-react';
import * as React from 'react';
import { inject, bind } from '../../../utils/decorators';
import { BpsReferenzkraftKalibrierungStore, BpsPositionRolle } from '../../../stores/BpsReferenzkraftKalibrierungStore';
import { Panel, Table, FormControl, FormGroup, Col, ControlLabel } from 'react-bootstrap';
import { round } from '../../../utils/round';
import { numberformat } from '../../../utils/numberformat';

@observer
class BpsReferenzkraftRollenDurchmesserMesspunkt extends React.Component<{ x: number, y: number}, {}> {
  @inject(BpsReferenzkraftKalibrierungStore) store: BpsReferenzkraftKalibrierungStore;
  @bind @action handleValueChanged(event: React.FormEvent<FormControl>) {
    const { x, y } = this.props;
    this.store.setMesswert(x, y, parseFloat((event.target as HTMLInputElement).value));
  }
  render() {
    const { x, y } = this.props;
    const { messwerte } = this.store.datensatz.rollendurchmesser;
    return (
      <td>
        <FormControl
          type="number"
          step="0.1"
          readOnly={this.store.readOnly}
          value={(messwerte[x][y] !== undefined && messwerte[x][y] !== null) ? messwerte[x][y] : ''}
          onChange={this.handleValueChanged}
        />
      </td>
    );
  }
}

@observer
class BpsReferenzkraftRollenDurchmesserMessreihe extends React.Component<{ index: number, caption: string}, {}> {
  render() {
    return (
      <tr>
        <td style={{whiteSpace: 'nowrap'}}>{this.props.caption}</td>
        {[0, 1, 2, 3].map(x => (<BpsReferenzkraftRollenDurchmesserMesspunkt key={x} x={x} y={this.props.index}/>))}
      </tr>
    );
  }
}

@observer
class BpsReferenzkraftRollenDurchmesserMittelwerte extends React.Component<{}, {}> {
  @inject(BpsReferenzkraftKalibrierungStore) kalibrierungStore: BpsReferenzkraftKalibrierungStore;
  @computed get nenndurchmesser() {
    const { kalibrierGegenstand } = this.kalibrierungStore;
    if (!kalibrierGegenstand) { return undefined; }
    let rollendurchmesser = kalibrierGegenstand.meta('rollendurchmesser');
    if (rollendurchmesser) {
      return parseFloat(rollendurchmesser);
    }
    return undefined;
  }
  renderMittelwert(wertabsolut: number | undefined, position: BpsPositionRolle) {
    if (!wertabsolut || !this.nenndurchmesser) { return <div>-</div>; }
    let durchmesserRelativ = round(wertabsolut / this.nenndurchmesser * 100, 1);
    let cssClass = durchmesserRelativ < 98 ? 'mittelwert-error' : 'mittelwert-success';
    return (
      <div className={cssClass}>{numberformat(wertabsolut)} (<span className={cssClass}>{numberformat(durchmesserRelativ)}%</span>)</div>
    );
  }
  render() {
    const { datensatz } = this.kalibrierungStore;
    return (
      <tr>
        <td>Mittelwert (wirksam)</td>
        <td colSpan={2}>
          {this.renderMittelwert(datensatz.rollendurchmesser.mittelwert.links, 'links')}
        </td>
        <td colSpan={2}>
          {this.renderMittelwert(datensatz.rollendurchmesser.mittelwert.rechts, 'rechts')}
        </td>
      </tr>
    );
  }
}

/**
 * Exportierte Komponente für Eingabe und Berechnungen Rollendurchmesser BPS für das Verfahren Referenzkraftmessung
 */
@observer
export default class BpsReferenzkraftRollenDurchmesser extends React.Component<{}, {}> {

  @inject(BpsReferenzkraftKalibrierungStore) store: BpsReferenzkraftKalibrierungStore;

  componentDidMount() {
    this.store.berechneMittelwerteRolle();
  }

  @bind @action handleOberflaecheChanged(event: React.FormEvent<FormControl>) {
    this.store.setOberflaeche((event.target as HTMLInputElement).value);
    this.store.berechneMittelwerteRolle();
  }

  @bind @action handleMessungChanged(event: React.FormEvent<FormControl>) {
    this.store.setMessart((event.target as HTMLInputElement).value);
    this.store.berechneMittelwerteRolle();
  }

  render() {
    return (
      <Panel header="Rollendurchmesser" collapsible={true} defaultExpanded={true}>
        {/* Optionen */}
        <Col sm={6}>
          <FormGroup controlId="Oberfläche">
            <Col componentClass={ControlLabel} sm={4}>Oberfläche</Col>
            <Col sm={8} className="text-va-middle">
              <FormControl componentClass="select"onChange={this.handleOberflaecheChanged} defaultValue={this.store.datensatz.oberflaeche} disabled={this.store.readOnly}>
                <option value="beschichtet">Beschichtet</option>
                <option value="geschweisst">Geschweißt</option>
              </FormControl>
            </Col>
          </FormGroup>
        </Col>
        <Col sm={6}>
          <FormGroup controlId="Messung">
            <Col componentClass={ControlLabel} sm={4}>Messung</Col>
            <Col sm={8}>
              <FormControl componentClass="select" onChange={this.handleMessungChanged} defaultValue={this.store.datensatz.messart} disabled={this.store.readOnly}>
                <option value="durchmesser">Durchmesser</option>
                <option value="umfang">Umfang</option>
              </FormControl>
            </Col>
          </FormGroup>
        </Col>
        {/* Messwerteingabe */}
        <Table striped={false} bordered={false} condensed={true} hover={false} className="bps-eingabe-rolle">
          <thead>
            <tr>
                <th/>
                <th colSpan={2}>Rolle links [mm]</th>
                <th colSpan={2}>Rolle rechts [mm]</th>
            </tr>
            <tr>
                <th>Messpunkt</th>
                <th>Vorne</th>
                <th>Hinten</th>
                <th>Vorne</th>
                <th>Hinten</th>
            </tr>
          </thead>
          <tbody>
            <BpsReferenzkraftRollenDurchmesserMessreihe index={0} caption="10 cm rechts der Mitte"/>
            <BpsReferenzkraftRollenDurchmesserMessreihe index={1} caption="Mitte der Rolle"/>
            <BpsReferenzkraftRollenDurchmesserMessreihe index={2} caption="10 cm links der Mitte"/>
            <BpsReferenzkraftRollenDurchmesserMittelwerte/>
          </tbody>
        </Table>
      </Panel>
    );
  }

}



// WEBPACK FOOTER //
// ./~/tslint-loader!./src/components/vorlagen/bps/BpsReferenzkraftRollenDurchmesser.tsx